/**
 * Player News API Service
 * Handles all API calls related to player news functionality
 */

// Helper function to get base URL
const getBaseUrl = () => import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;

// Function to create request headers with auth token
const createHeaders = (token: string) => ({
  'Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json'
});

export interface PlayerNewsParams {
  limit?: number;
  offset?: number;
  min_relevance?: 'high' | 'medium' | 'low';
  recency?: 'very_recent' | 'recent' | 'somewhat_recent' | 'not_recent';
  source_type?: 'direct_search' | 'index_followup';
  sort_by?: 'relevance' | 'recency' | 'publication_date';
  sort_order?: 'asc' | 'desc';
}

export interface NewsArticle {
  id: string;
  player_id: string;
  headline: string;
  article_url: string;
  source_name: string;
  publication_date: string;
  snippet: string;
  relevance: string;
  source_type: string;
  mentioned_entities?: Array<{
    name: string;
    type: string;
  }>;
  original_headline?: string;
  headline_source?: string;
  llm_publication_date?: string;
  llm_publication_time?: string;
  recency_assessment?: string;
  fetched_at: string;
}

export interface PlayerNewsResponse {
  items: NewsArticle[];
  totalCount: number;
  limit: number;
  offset: number;
}

/**
 * Fetch news articles for a player
 */
export const fetchPlayerNews = async (
  playerId: string,
  token: string,
  params: PlayerNewsParams = {}
): Promise<PlayerNewsResponse> => {
  const baseUrl = getBaseUrl();
  const queryParams = new URLSearchParams();
  
  if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
  if (params.offset !== undefined) queryParams.append('offset', params.offset.toString());
  if (params.min_relevance) queryParams.append('min_relevance', params.min_relevance);
  if (params.recency) queryParams.append('recency', params.recency);
  if (params.source_type) queryParams.append('source_type', params.source_type);
  if (params.sort_by) queryParams.append('sort_by', params.sort_by);
  if (params.sort_order) queryParams.append('sort_order', params.sort_order);
  
  const response = await fetch(`${baseUrl}/ai/v1/player/${playerId}/news?${queryParams.toString()}`, {
    method: 'GET',
    headers: createHeaders(token)
  });
  
  if (!response.ok) {
    throw new Error(`Error fetching player news: ${response.statusText}`);
  }
  
  return await response.json();
};